import { useState } from "react";
import { Plus, Pencil, Trash2, Megaphone, ExternalLink } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";

const emptyForm = {
  title: "",
  image_url: "",
  link_url: "",
  placement: "bottom",
  active: true,
  position: 0,
  starts_at: "",
  expires_at: "",
};

const AdminAdBanners = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const { data: banners = [], isLoading } = useQuery({
    queryKey: ["admin-ad-banners"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ad_banners")
        .select("*")
        .order("position", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const upsert = useMutation({
    mutationFn: async (banner: typeof emptyForm & { id?: string }) => {
      const payload = {
        ...banner,
        starts_at: banner.starts_at || null,
        expires_at: banner.expires_at || null,
      };
      if (banner.id) {
        const { error } = await (supabase.from("ad_banners") as any).update(payload).eq("id", banner.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase.from("ad_banners") as any).insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-ad-banners"] });
      toast.success(editing ? "Banner updated" : "Banner created");
      setEditing(null);
      setCreating(false);
      setForm(emptyForm);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from("ad_banners") as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-ad-banners"] });
      toast.success("Banner deleted");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openCreate = () => { setForm(emptyForm); setEditing(null); setCreating(true); };
  const openEdit = (b: any) => {
    setForm({
      title: b.title,
      image_url: b.image_url,
      link_url: b.link_url,
      placement: b.placement,
      active: b.active,
      position: b.position,
      starts_at: b.starts_at ? b.starts_at.slice(0, 16) : "",
      expires_at: b.expires_at ? b.expires_at.slice(0, 16) : "",
    });
    setEditing(b);
    setCreating(true);
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.image_url.trim() || !form.link_url.trim()) {
      toast.error("Title, image URL, and link URL are required");
      return;
    }
    upsert.mutate(editing ? { ...form, id: editing.id } : form);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-6 h-6" /> Ad Banners
          </h1>
          <p className="text-muted-foreground text-sm">{banners.length} banner{banners.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> New Banner</Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-10 text-center">Loading…</div>
      ) : banners.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No ad banners yet</p>
        </div>
      ) : (
        <div className="bg-card rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="p-3 font-medium">Banner</th>
                <th className="p-3 font-medium hidden md:table-cell">Placement</th>
                <th className="p-3 font-medium hidden md:table-cell">Status</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((b: any) => (
                <tr key={b.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img src={b.image_url} alt="" className="w-16 h-10 rounded object-cover border" />
                      <div>
                        <p className="font-medium truncate max-w-[200px]">{b.title}</p>
                        <a href={b.link_url} target="_blank" rel="noopener" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-primary">
                          {b.link_url.slice(0, 40)}… <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <span className="capitalize bg-secondary px-2 py-0.5 rounded text-xs">{b.placement}</span>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <span className={`text-xs font-medium ${b.active ? "text-green-600" : "text-muted-foreground"}`}>
                      {b.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(b)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => { if (window.confirm(`Delete "${b.title}"?`)) deleteMut.mutate(b.id); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Banner" : "New Ad Banner"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Banner title" />
            </div>
            <div>
              <Label>Image URL *</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label>Link URL *</Label>
              <Input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label>Placement</Label>
              <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Top of post</SelectItem>
                  <SelectItem value="bottom">Bottom of post</SelectItem>
                  <SelectItem value="sidebar">Sidebar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={form.position} onChange={(e) => setForm({ ...form, position: +e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Starts At</Label>
                <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
              </div>
              <div>
                <Label>Expires At</Label>
                <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(checked) => setForm({ ...form, active: checked })} />
              <Label>Active</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={upsert.isPending}>
                {upsert.isPending ? "Saving…" : editing ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAdBanners;
